import { AppStore } from '../state/store';
import { documentToFile, fileToDocument, emptyDocument } from '../core/doc/document';
import { convertRct3Dat } from '../core/rct3/convert';
import { defaultRides } from '../presets/defaultRides';
import {
  deleteLocalCoaster,
  listLocalCoasters,
  loadLocalCoaster,
  saveLocalCoaster,
} from '../storage/localCoasters';
import { flashHint } from './toolbar';
import { bindPanelCollapse, panelEnd, panelStart } from './collapsiblePanel';
import { sidebarPane } from './sidebarTabs';

/** Save / load via browser localStorage; DefaultRides presets; export/import as files. */
export class FilePanel {
  private el: HTMLElement;

  constructor(private store: AppStore) {
    this.el = document.createElement('div');
    sidebarPane('file').appendChild(this.el);
    store.on('doc', () => this.render());
    this.render();
  }

  private render(): void {
    this.el.innerHTML = `
      ${panelStart('file', 'File')}
        <div class="row"><input type="text" id="coaster-name" value="${escapeAttr(this.store.doc.meta.name)}" style="flex:1"></div>
        <div class="row">
          <button data-act="save">Save</button>
          <button data-act="export">Export</button>
          <button data-act="import">Import</button>
          <button data-act="import-rct3" title="Import RCT3 park/track .dat">RCT3 Import</button>
        </div>
        <div class="row">
          <button data-act="new">New</button>
        </div>
        <h4 style="margin:10px 0 4px;font-size:12px;color:#9aa7b5;font-weight:600">Default rides</h4>
        <div class="file-list" id="default-rides-list"></div>
        <h4 style="margin:10px 0 4px;font-size:12px;color:#9aa7b5;font-weight:600">Saved</h4>
        <div class="file-list" id="file-list"></div>
        <input type="file" id="import-input" accept=".json,.rcsim.json" style="display:none">
        <input type="file" id="rct3-import-input" accept=".dat,application/octet-stream" style="display:none">
      ${panelEnd()}`;

    bindPanelCollapse(this.el);

    const nameInput = this.el.querySelector<HTMLInputElement>('#coaster-name')!;
    nameInput.addEventListener('change', () => {
      this.store.doc.meta.name = nameInput.value;
    });

    this.el.querySelector('[data-act="save"]')!.addEventListener('click', () => this.save());
    this.el.querySelector('[data-act="export"]')!.addEventListener('click', () => this.export());
    this.el.querySelector('[data-act="new"]')!.addEventListener('click', () => {
      if (confirm('Start a new empty coaster? Unsaved changes are kept in undo history.')) {
        this.store.setDocument(emptyDocument());
      }
    });

    const importInput = this.el.querySelector<HTMLInputElement>('#import-input')!;
    this.el.querySelector('[data-act="import"]')!.addEventListener('click', () => importInput.click());
    importInput.addEventListener('change', async () => {
      const file = importInput.files?.[0];
      if (!file) return;
      try {
        const doc = fileToDocument(JSON.parse(await file.text()));
        this.store.setDocument(doc);
        flashHint(`Imported "${doc.meta.name}"`);
      } catch (err) {
        alert(`Import failed: ${err instanceof Error ? err.message : err}`);
      }
      importInput.value = '';
    });

    const rct3Input = this.el.querySelector<HTMLInputElement>('#rct3-import-input')!;
    this.el.querySelector('[data-act="import-rct3"]')!.addEventListener('click', () => rct3Input.click());
    rct3Input.addEventListener('change', async () => {
      const file = rct3Input.files?.[0];
      if (!file) return;
      try {
        flashHint('Importing RCT3…');
        const buf = new Uint8Array(await file.arrayBuffer());
        const coasterFile = convertRct3Dat(buf, { sourceName: file.name });
        const doc = fileToDocument(coasterFile);
        this.store.setDocument(doc);
        flashHint(`Imported RCT3 "${doc.meta.name}"`);
      } catch (err) {
        alert(`RCT3 import failed: ${err instanceof Error ? err.message : err}`);
      }
      rct3Input.value = '';
    });

    this.refreshDefaultRides();
    this.refreshList();
  }

  private refreshDefaultRides(): void {
    const listEl = this.el.querySelector('#default-rides-list');
    if (!listEl) return;
    listEl.innerHTML = defaultRides.length
      ? ''
      : '<div style="color:#7f8b99;cursor:default">No DefaultRides found</div>';
    for (const ride of defaultRides) {
      const row = document.createElement('div');
      row.style.display = 'flex';
      row.style.alignItems = 'center';
      row.style.gap = '6px';
      row.style.cursor = 'pointer';
      row.title = ride.blurb;
      row.textContent = ride.name;
      row.addEventListener('click', () => {
        this.store.setDocument(ride.build());
        flashHint(`Loaded "${ride.name}"`);
      });
      listEl.appendChild(row);
    }
  }

  private id(): string {
    return (
      this.store.doc.meta.name
        .toLowerCase()
        .replace(/[^a-z0-9-_]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 64) || 'untitled'
    );
  }

  private save(): void {
    try {
      const id = this.id();
      saveLocalCoaster(id, documentToFile(this.store.doc));
      flashHint(`Saved as "${id}"`);
      this.refreshList();
    } catch (err) {
      flashHint(`Save failed: ${err instanceof Error ? err.message : err}`);
    }
  }

  private export(): void {
    const blob = new Blob([JSON.stringify(documentToFile(this.store.doc), null, 2)], {
      type: 'application/json',
    });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${this.id()}.rcsim.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  private refreshList(): void {
    const listEl = this.el.querySelector('#file-list');
    if (!listEl) return;
    const items = listLocalCoasters();
    listEl.innerHTML = items.length
      ? ''
      : '<div style="color:#7f8b99;cursor:default">No saved coasters yet</div>';
    for (const item of items) {
      const row = document.createElement('div');
      row.style.display = 'flex';
      row.style.alignItems = 'center';
      row.style.gap = '6px';

      const label = document.createElement('div');
      label.style.flex = '1';
      label.style.cursor = 'pointer';
      label.textContent = item.name || item.id;
      label.title = 'Click to load';
      label.addEventListener('click', () => {
        const data = loadLocalCoaster(item.id);
        if (!data) {
          flashHint(`"${item.id}" not found`);
          this.refreshList();
          return;
        }
        try {
          this.store.setDocument(fileToDocument(data));
          flashHint(`Loaded "${item.name || item.id}"`);
        } catch (err) {
          alert(`Load failed: ${err instanceof Error ? err.message : err}`);
        }
      });

      const del = document.createElement('button');
      del.textContent = '×';
      del.title = 'Delete from this browser';
      del.style.padding = '0 6px';
      del.addEventListener('click', (e) => {
        e.stopPropagation();
        if (!confirm(`Delete saved coaster "${item.name || item.id}"?`)) return;
        deleteLocalCoaster(item.id);
        flashHint(`Deleted "${item.id}"`);
        this.refreshList();
      });

      row.append(label, del);
      listEl.appendChild(row);
    }
  }
}

function escapeAttr(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}
